import { useState } from 'react';
import { Hourglass, List, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PostingArrivalSurface } from '@features/notifications/components/PostingArrivalSurface';
import { PostingRecoveryContent } from '@shared/posting/PostingRecoveryContent';
import { issuanceBatchRecoveryView } from '@shared/posting/postingRecovery';
import { cn } from '@/lib/utils';
import { StatusPill } from './PicklistProgressBits';
import type { PicklistDto, PicklistItemDto, PostingBatchStatusDto } from '../types';
import { PostingStatusEnum } from '../types';
import { batchChips, qty, unitBreakdown } from '../utils/picklistProgress';

/**
 * PW-17 — the in-flight posting batches strip.
 *
 * ## The one rule this file must not break
 *
 * ⛔ **The reach decision and every word of failure copy come from the SHARED `PostingRecoveryContent`.**
 * Whether a failed batch may already exist in NetSuite decides whether the next click DUPLICATES a record
 * in the live company file, and that rule had four private copies before N8 collapsed them into one. This
 * component owns the batch's IDENTITY and CONTEXT — which batch, which lines, how long ago — and hands the
 * verdict to the shared component. Do not add a `postReach >= 2` test here.
 *
 * ## What is deliberately absent
 *
 * The design draws a **connection pill** (Live / Reconnecting… / Offline). A real connection state exists
 * (`useNotificationEventStream`), but it is mounted ONCE in `AppLayout` and exposes its value only through
 * its own return — reading it here would open a second SSE stream for a decorative pill. Plumbing it into a
 * store is a notification-feature change, not this ticket. Omitted rather than faked.
 */

const VISIBLE_LIMIT = 3;
/** A batch working this long is slow, not stuck — and saying so stops a second Post. */
const SLOW_AFTER_MS = 10 * 60 * 1000;

interface PostingBatchesStripProps {
  picklist: PicklistDto;
  resolvingBatchId: number | null;
  onRetry: (batchId: number) => void;
  onCancel: (batchId: number) => void;
  // PT-6 scope 14c (O-48) — "Fix quantity" is gone. Under C3 Adjust refuses an item a batch still holds, so the
  // button could only ever open a refusal. The batch keeps Retry and Cancel; after a Cancel, the item's Adjust is
  // available in the Items table.
}

/** Lines this batch is holding, from PW-01's per-item batch ids. */
function linesInBatch(picklist: PicklistDto, batchId: number): PicklistItemDto[] {
  return (picklist.items ?? []).filter((i) => (i.postingBatchIds ?? []).includes(batchId));
}

function ageLabel(claimedAt: string | null): string {
  if (!claimedAt) return '';
  const ms = Date.now() - new Date(claimedAt).getTime();
  const minutes = Math.max(0, Math.round(ms / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
}

function pressedAt(claimedAt: string | null): string {
  if (!claimedAt) return '';
  return new Date(claimedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function PostingBatchesStrip({
  picklist,
  resolvingBatchId,
  onRetry,
  onCancel,
}: PostingBatchesStripProps) {
  const [showAll, setShowAll] = useState(false);
  const batches = picklist.activePostingBatches ?? [];
  if (batches.length === 0) return null;

  const failed = batches.filter((b) => b.status === PostingStatusEnum.PostingFailed);
  const posting = batches.filter((b) => b.status === PostingStatusEnum.Posting);
  // Per unit, never one flat total: this summed postingQuantity across every line regardless of unit of
  // measure and printed it as "N units", so a picklist of 50 kg + 3 pcs read "53 units" — the one figure
  // D30 set out to keep off the screen, on the surface that names an in-flight NetSuite write.
  const postingByUnit = unitBreakdown(picklist.items ?? [], (i) => i.postingQuantity);

  // The same normalised views feed the inline rows AND the floating panel a notification arrival raises,
  // so the two can never disagree on screen.
  const views = failed.map((b) => issuanceBatchRecoveryView({
    id: b.id,
    status: 'Failed',
    postReach: b.postReach,
    netSuiteRecordId: b.netSuiteRecordId,
    lastError: b.postingFailedReason,
  }));

  // Failures first: they are the rows that need a person.
  const ordered = [...failed, ...posting];
  const visible = ordered.slice(0, VISIBLE_LIMIT);
  const hidden = ordered.length - visible.length;

  const subLine = [
    `${postingByUnit} posting through them — locked until NetSuite confirms`,
    ...(failed.length > 0 ? [`${failed.length} needing a fix`] : []),
  ].join(' · ');

  return (
    <PostingArrivalSurface
      isBusy={resolvingBatchId !== null}
      views={views}
      // ⚠️ The view's `jobId` is null on MIS — `PostingBatchStatusDto` carries no outbox job id — so
      // recovery is dispatched by BATCH id. That is also why an arrival flashes the whole section rather
      // than one batch: the wire cannot tell us which batch the notification was about.
      onRetry={(v) => v.sourceId != null && onRetry(v.sourceId)}
      onReconcile={(v) => v.sourceId != null && onRetry(v.sourceId)}
      onCancel={(v) => { if (v.sourceId != null) onCancel(v.sourceId); }}
    >
      <div className="overflow-hidden rounded-xl border border-info/30 bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold">
              {ordered.length} batch{ordered.length === 1 ? '' : 'es'} in flight
            </p>
            <p className="text-xs text-muted-foreground">{subLine}</p>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className="cursor-help text-xs text-muted-foreground underline decoration-dotted underline-offset-4"
              >
                What does posting mean?
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              These units have been handed to a posting batch and are on their way to NetSuite. They are
              locked — not adjustable, and excluded from the next batch — so they cannot be issued twice.
              NetSuite inventory moves only when the batch settles.
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="divide-y divide-border">
          {visible.map((batch) => (
            <BatchRow
              key={batch.id}
              picklist={picklist}
              batch={batch}
              view={views[failed.indexOf(batch)]}
              isBusy={resolvingBatchId === batch.id}
              onRetry={onRetry}
              onCancel={onCancel}
            />
          ))}
        </div>

        {hidden > 0 && (
          <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-4 py-2.5">
            <span className="text-xs text-muted-foreground">
              {hidden} more batch{hidden === 1 ? '' : 'es'} not shown
            </span>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowAll(true)}>
              <List className="mr-1.5 h-3.5 w-3.5" />
              See all {ordered.length}
            </Button>
          </div>
        )}
      </div>

      <AllBatchesDialog
        open={showAll}
        onOpenChange={setShowAll}
        picklist={picklist}
        batches={ordered}
      />
    </PostingArrivalSurface>
  );
}

function BatchRow({
  picklist,
  batch,
  view,
  isBusy,
  onRetry,
  onCancel,
}: {
  picklist: PicklistDto;
  batch: PostingBatchStatusDto;
  view: ReturnType<typeof issuanceBatchRecoveryView> | undefined;
  isBusy: boolean;
  onRetry: (batchId: number) => void;
  onCancel: (batchId: number) => void;
}) {
  // PT-7 A11 — one chip per (item, MRS line), from the batch's frozen lines when they resolved.
  const chips = batchChips(picklist, batch.id);
  const isFailed = batch.status === PostingStatusEnum.PostingFailed;
  const claimedMs = batch.postingClaimedAt ? Date.now() - new Date(batch.postingClaimedAt).getTime() : 0;
  const isSlow = !isFailed && claimedMs >= SLOW_AFTER_MS;
  const batchQty = chips.reduce((acc, c) => acc + c.quantity, 0);

  return (
    <div className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(140px,auto)_minmax(0,1fr)]">
      <div className="space-y-1">
        <StatusPill
          state={isFailed
            ? { tone: 'destructive', label: 'Failed', tooltip: 'This batch did not complete. See the note beside it.' }
            : { tone: 'info', label: 'Posting', tooltip: 'This batch is on its way to NetSuite.' }}
        />
        <p className="font-mono text-xs font-semibold text-muted-foreground">Batch #{batch.id}</p>
      </div>

      <div className="min-w-0 space-y-1.5">
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((chip, index) => (
              <span key={`${chip.lineId}-${index}`} className="inline-flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-0.5 text-xs">
                <span className="font-mono">{chip.itemCode}</span>
                {chip.mrsNumber && (
                  <span className="font-mono text-[10.5px] font-medium text-info">{chip.mrsNumber}</span>
                )}
                <span className="text-muted-foreground">
                  {qty(chip.quantity)} {chip.unitOfMeasure}
                </span>
              </span>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {batchQty > 0 && `${qty(batchQty)} · `}
          {chips.length} line{chips.length === 1 ? '' : 's'}
          {batch.postingClaimedAt && ` · pressed ${pressedAt(batch.postingClaimedAt)} · ${ageLabel(batch.postingClaimedAt)}`}
        </p>

        {/* ⛔ The failure copy and the reach verdict are the SHARED component's, not this file's. */}
        {isFailed && view && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2">
            <PostingRecoveryContent
              view={view}
              isBusy={isBusy}
              // MIS has ONE server-side re-arm serving both verbs — the adopt probe branches internally —
              // so the same handler serves both, and the shared core picks the label that tells the user
              // which of the two they are doing.
              onRetry={() => onRetry(batch.id)}
              onReconcile={() => onRetry(batch.id)}
              onCancel={() => onCancel(batch.id)}
            />
          </div>
        )}

        {isSlow && (
          <p className="flex items-start gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            <Hourglass className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            Slower than usual. NetSuite only allows a couple of writes at a time, so this is waiting its
            turn — it is not stuck, and you do not need to press Post again.
          </p>
        )}

        {!isFailed && !isSlow && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden />
            Creating the Inventory Adjustment in NetSuite…
          </p>
        )}
      </div>

    </div>
  );
}

/** §8 — every batch, when the strip shows only the first three. */
function AllBatchesDialog({
  open,
  onOpenChange,
  picklist,
  batches,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  picklist: PicklistDto;
  batches: PostingBatchStatusDto[];
}) {
  const [filter, setFilter] = useState<'all' | 'posting' | 'failed'>('all');

  const counts = {
    all: batches.length,
    posting: batches.filter((b) => b.status === PostingStatusEnum.Posting).length,
    failed: batches.filter((b) => b.status === PostingStatusEnum.PostingFailed).length,
  };
  const shown = batches.filter((b) => filter === 'all'
    || (filter === 'posting' && b.status === PostingStatusEnum.Posting)
    || (filter === 'failed' && b.status === PostingStatusEnum.PostingFailed));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-[1000px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">Posting batches</DialogTitle>
          <DialogDescription>
            Every open batch for <span className="font-mono">{picklist.picklistNumber}</span>. Their units are
            locked until NetSuite confirms them.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-1.5">
            {/* Only non-empty states get a chip — a filter that can only ever return nothing is noise. */}
            {(['all', 'posting', 'failed'] as const)
              .filter((key) => key === 'all' || counts[key] > 0)
              .map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={filter === key}
                  onClick={() => setFilter(key)}
                  className={cn(
                    'h-7 rounded-full border px-3 text-xs capitalize transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    filter === key
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-muted',
                  )}
                >
                  {key} {counts[key]}
                </button>
              ))}
          </div>
          <span className="text-xs text-muted-foreground">
            Showing {shown.length} of {batches.length}
          </span>
        </div>

        <div className="divide-y divide-border rounded-lg border border-border">
          <div className="grid gap-3 bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground md:grid-cols-[minmax(120px,150px)_80px_minmax(150px,1fr)_minmax(88px,120px)]">
            <span>State</span>
            <span>Batch</span>
            <span>Items posting</span>
            <span>Age</span>
          </div>
          {shown.map((batch) => {
            const lines = linesInBatch(picklist, batch.id);
            return (
              <div
                key={batch.id}
                className="grid gap-3 px-3 py-2.5 text-sm md:grid-cols-[minmax(120px,150px)_80px_minmax(150px,1fr)_minmax(88px,120px)]"
              >
                <StatusPill
                  state={batch.status === PostingStatusEnum.PostingFailed
                    ? { tone: 'destructive', label: 'Failed', tooltip: 'This batch did not complete.' }
                    : { tone: 'info', label: 'Posting', tooltip: 'On its way to NetSuite.' }}
                />
                <span className="font-mono text-xs">#{batch.id}</span>
                <span className="min-w-0 truncate text-xs">
                  {lines.length > 0
                    ? lines.map((l) => `${l.itemCode} ${qty(l.postingQuantity)}`).join(' · ')
                    : '—'}
                </span>
                <span className="text-xs text-muted-foreground">{ageLabel(batch.postingClaimedAt)}</span>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
